<template>
  <div class="px-3">
    <table v-if="show_header_section">
      <thead>
        <tr>
          <th style="width: 25%">
            {{
              $getVisibleNames("mesh.career", false, "Programa").toUpperCase()
            }}
          </th>
          <td colspan="3" style="padding: 0 16px">
            <template v-if="career">{{ career.name }}</template>
          </td>
          <th rowspan="2" style="width: 15%">
            {{ $t("reusable.common.numberAbbreviation") }}
            {{
              $getVisibleNames(
                "teaching.section",
                false,
                "Sección"
              ).toUpperCase()
            }}
          </th>
          <td rowspan="2" colspan="2" style="width: 5%">
            {{ section ? section.name : "-" }}
          </td>
        </tr>
        <tr>
          <th>
            {{
              $getVisibleNames(
                "mesh.egressprofilematter",
                false,
                "Asignatura"
              ).toUpperCase()
            }}
          </th>
          <td colspan="3" v-if="oldMatter">
            <b-link
              :to="{
                name: 'MatterProgramView',
                params: { matter_id: Matter.id },
              }"
              >{{ oldMatter.name }}</b-link
            >
          </td>
        </tr>
      </thead>
    </table>
    <table class="mt-2">
      <tbody>
        <b-tr v-if="!show_header_section">
          <b-th class="w-25">
            {{
              $getVisibleNames(
                "teaching.section",
                false,
                "Sección"
              ).toUpperCase()
            }}</b-th
          >
          <b-td class="w-75" colspan="3">
            {{ section ? section.name : "-" }}
          </b-td>
        </b-tr>
        <tr v-for="professor in professors" :key="professor.id">
          <th style="width: 25%">
            {{ $t("dashboard.dashboardView.teacherName").toUpperCase() }}
          </th>
          <td style="width: 25%">
            {{ professor.first_name + " " + professor.last_name }}
          </td>
          <th style="width: 25%">
            {{ $t("reusable.common.email2").toUpperCase() }}
          </th>
          <td style="width: 25%">{{ professor.email }}</td>
        </tr>
        <tr>
          <th style="width: 25%">
            {{
              $t(
                "dashboard.dashboardView.studentAttentionSchedule"
              ).toUpperCase()
            }}
          </th>
          <td colspan="3" class="td-header text-left px-3">
            <template v-if="allows_crud">
              <StyledTextArea
                v-model="attention_hours"
                @input="debouncePatchSection"
              ></StyledTextArea>
            </template>
            <template v-else-if="section">{{
              section.attention_hours
            }}</template>
          </td>
        </tr>
        <tr>
          <th style="width: 25%">
            {{
              $t("dashboard.dashboardView.coexistenceRulesForThe").toUpperCase()
            }}
            {{
              $getVisibleNames(
                "mesh.egressprofilematter",
                false,
                "Asignatura"
              ).toUpperCase()
            }}
          </th>
          <td colspan="3" class="td-header text-left px-3">
            <template v-if="allows_crud">
              <StyledTextArea
                v-model="coexistence_rules"
                @input="debouncePatchSection"
              ></StyledTextArea>
            </template>
            <template v-else-if="section">{{
              section.coexistence_rules
            }}</template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";

export default {
  name: "HeaderSection",
  components: {
    StyledTextArea: () => import("@/components/reusable/StyledTextArea"),
  },
  props: {
    Matter: {
      type: Object,
      required: true,
    },
    section_id: {
      type: Number,
    },
    allows_crud: {
      type: Boolean,
      default: false,
    },
    show_header_section: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      attention_hours: "",
      coexistence_rules: "",
    };
  },
  computed: {
    ...mapGetters({
      campuses: names.CAMPUSES,
      matters: names.MATTERS,
      egressProfiles: names.EGRESS_PROFILES,
      careers: names.CAREERS,
      sections: names.SECTIONS,
    }),
    egressProfile() {
      return this.egressProfiles.find(
        (x) => x.id == this.Matter.egress_profile
      );
    },
    oldMatter() {
      if (!this.Matter) return null;
      return this.matters.find((x) => x.id == this.Matter.matter);
    },
    career() {
      if (!this.egressProfile) return null;
      return this.careers.find((x) =>
        x.egress_profiles.includes(this.egressProfile.id)
      );
    },
    section() {
      return this.sections.find((x) => x.id == this.section_id);
    },
    professors() {
      if (!this.section) return [];
      return this.section.professors_detail || [];
    },
    campus() {
      if (!this.section) return null;
      return this.campuses.find((x) => x.id == this.section.campus);
    },
  },
  methods: {
    fetchProfessors() {
      if (!this.section_id) return;
      // Los datos del docente vienen anidados en la sección, sin pasar por el endpoint de usuarios
      this.$store.dispatch(names.FETCH_SECTION, {
        section_id: this.section_id,
        expand: ["professors"],
      });
    },
    debouncePatchSection() {
      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        const payload = {
          section_id: this.section.id,
          item: {
            attention_hours: this.attention_hours,
            coexistence_rules: this.coexistence_rules,
          },
        };
        this.$store.dispatch(names.PATCH_SECTION, payload).then(() => {
          toast(
            this.$t("evaluations.newEvaluations.entityUpdatedFeminine", {
              entity: this.$getVisibleNames(
                "teaching.section",
                false,
                "Sección"
              ),
            })
          );
        });
      }, 2000);
    },
  },
  watch: {
    section() {
      if (this.section) {
        this.attention_hours = this.section.attention_hours;
        this.coexistence_rules = this.section.coexistence_rules;
      }
    },
  },
  created() {
    if (
      this.egressProfiles.findIndex(
        (x) => x.id == this.Matter.egress_profile
      ) == -1
    ) {
      this.$store
        .dispatch(names.FETCH_EGRESS_PROFILE, this.Matter.egress_profile)
        .then((response) => {
          if (this.careers.findIndex((x) => x.id == response.career) == -1) {
            this.$store.dispatch(names.FETCH_CAREER, response.career);
          }
          if (
            response.campus &&
            this.campuses.findIndex((x) => x.id == response.campus) == -1
          ) {
            this.$store.dispatch(names.FETCH_CAMPUS, response.campus);
          }
        });
    }
    this.fetchProfessors();
    if (this.section) {
      this.attention_hours = this.section.attention_hours;
      this.coexistence_rules = this.section.coexistence_rules;
    }
  },
};
</script>

<style scoped>
table {
  /* position: relative; */
  width: 100%;
  border: 2px solid var(--kl-table-border);
  font-size: var(--primary-font-size);
}
th {
  vertical-align: middle;
  border: 1px solid var(--kl-table-border);
  background-color: var(--kl-color-accent-medium) !important;
  color: white;
  /* padding: 0px 5px; */
  position: sticky;
  padding-left: 1em;
  text-align: left;
  font-size: var(--secondary-font-size);
}
td {
  text-align: left;
  padding-left: 0.5em;
  border: 1px solid var(--kl-table-border);
}
.td-header {
  background-color: var(--kl-color-accent-medium);
  color: var(--kl-text-on-primary);
}
p {
  text-align: justify;
}
.campus-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  height: fit-content;
  padding: 4px 6px;
  border: none;
}
.campus-div {
  background-color: var(--kl-chip-bg);
  color: var(--kl-chip-text);
  border: 1px solid var(--kl-chip-border);
  border-radius: var(--kl-radius-pill);
  margin: 0 4px;
  margin-top: 2%;
  margin-bottom: -2.8em;
  padding: 1px 4px;
  padding-top: 2.5%;
  flex-grow: 1;
  font-size: var(--kl-chip-fs);
  font-weight: var(--kl-chip-fw);
  text-align: center;
}
</style>